import React, { useState } from "react";
import { format_date, toFixed } from "../CustomComponent";
import {
  ADVICE_LEVEL_STYLE,
  blockStyle,
  cardStyle,
  DEFAULT_INCOME_TAG_COLOR,
  formatTaxId,
  formBlockStyle,
  incomeTagColor,
  monthLabel,
  RD_GREEN_DARK,
} from "./constants";

// ---- การ์ดข้อมูลผู้มีเงินได้ (ดึงจากข้อมูลพนักงานในระบบ) ----
const RD_GREEN_TEXT = "#1f5f24";

const profileCardStyle = {
  background: "linear-gradient(135deg, #ffffff 0%, #f3faf4 55%, #e8f5ea 100%)",
  border: "1px solid rgba(46, 125, 50, 0.16)",
  borderRadius: 20,
  boxShadow: "0 12px 28px rgba(46, 125, 50, 0.08)",
};

const avatarStyle = {
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: "linear-gradient(145deg, #4caf50 0%, #2e7d32 100%)",
  color: "#ffffff",
  fontSize: "1.25rem",
  boxShadow: "0 8px 18px rgba(46, 125, 50, 0.28)",
  flexShrink: 0,
};

const infoIconStyle = {
  width: 34,
  height: 34,
  borderRadius: 10,
  background: "rgba(76, 175, 80, 0.12)",
  color: RD_GREEN_DARK,
  flexShrink: 0,
};

// อักษรย่อจากชื่อ ใช้ตัวแรกของชื่อและนามสกุล ถ้าไม่มีชื่อโชว์ไอคอนคน
const initials = (fullName) => {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return null;
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("");
};

// อายุปัจจุบันจากวันเกิด แสดงต่อท้ายวันเกิดให้เช็กสิทธิ์ผู้สูงอายุได้ทันที
const ageOf = (birthday) => {
  if (!birthday) return null;
  const birth = new Date(birthday);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? age : null;
};

// 2 บรรทัดเสมอ: ป้ายชื่อ + ค่า (ข้อมูลเสริมเช่นอายุใส่ต่อท้ายค่าในบรรทัดเดียวกัน)
const InfoItem = ({ icon, label, value, suffix, mono, muted }) => (
  <div className="col-12 col-md-6 col-xl-4">
    <div className="d-flex align-items-center gap-3">
      <div className="d-flex align-items-center justify-content-center" style={infoIconStyle}>
        <i className={`fa-solid ${icon}`} style={{ fontSize: "0.85rem" }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="text-secondary" style={{ fontSize: "0.72rem", letterSpacing: "0.02em" }}>
          {label}
        </div>
        <div
          className={muted ? "text-secondary" : "fw-semibold"}
          style={{
            color: muted ? undefined : "#12314d",
            letterSpacing: mono ? "0.06em" : undefined,
            wordBreak: "break-word",
          }}
          title={typeof value === "string" ? value : undefined}
        >
          {value || "-"}
          {suffix ? (
            <span className="fw-normal text-secondary ms-2" style={{ fontSize: "0.8rem", letterSpacing: 0 }}>
              {suffix}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  </div>
);

const TaxpayerCard = ({ taxpayer, year }) => {
  const abbr = initials(taxpayer?.full_name);
  const age = ageOf(taxpayer?.birthday);
  const subtitle = [taxpayer?.employee_code ? `รหัสพนักงาน ${taxpayer.employee_code}` : null, taxpayer?.company_name].filter(Boolean).join(" · ");

  return (
    <div className="p-3 p-md-4 mb-4" style={profileCardStyle}>
      <div className="d-flex align-items-center gap-3 flex-wrap">
        <div className="d-flex align-items-center justify-content-center fw-bold" style={avatarStyle}>
          {abbr || <i className="fa-solid fa-user" />}
        </div>
        <div className="flex-grow-1" style={{ minWidth: 200 }}>
          <div
            className="text-secondary"
            style={{
              fontSize: "0.72rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            ข้อมูลผู้มีเงินได้
          </div>
          <div
            className="fw-bold"
            style={{
              color: RD_GREEN_TEXT,
              fontSize: "clamp(1.05rem, 2vw, 1.3rem)",
              lineHeight: 1.25,
            }}
          >
            {taxpayer?.full_name || "-"}
          </div>
          {subtitle ? <div className="small text-secondary">{subtitle}</div> : null}
        </div>
        <span
          className="badge rounded-pill fw-semibold px-3 py-2"
          style={{
            background: "#ffffff",
            color: RD_GREEN_DARK,
            border: "1px solid rgba(46, 125, 50, 0.25)",
            fontSize: "0.78rem",
          }}
        >
          <i className="fa-regular fa-calendar me-2" />
          ปีภาษี {year}
        </span>
      </div>

      <div
        className="my-3"
        style={{
          height: 1,
          background: "linear-gradient(90deg, rgba(46,125,50,0.25), rgba(46,125,50,0))",
        }}
      />

      <div className="row g-3">
        <InfoItem
          icon="fa-id-card"
          label="เลขประจำตัวผู้เสียภาษีอากร"
          value={taxpayer?.national_id ? formatTaxId(taxpayer.national_id) : "ยังไม่มีเลขในระบบ แจ้ง HR เพิ่มให้"}
          mono={!!taxpayer?.national_id}
          muted={!taxpayer?.national_id}
        />
        <InfoItem
          icon="fa-cake-candles"
          label="วันเดือนปีเกิด"
          value={taxpayer?.birthday ? format_date(taxpayer.birthday, "dd/mm/yyyy") : ""}
          suffix={age !== null ? `(อายุ ${age} ปี)` : null}
        />
        <InfoItem icon="fa-location-dot" label="สถานที่ติดต่อ" value={taxpayer?.address} />
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color, hint, emphasize }) => (
  <div className="col-6 col-lg-4 col-xxl-2">
    <div
      className="rounded-4 p-3 h-100"
      style={
        emphasize
          ? {
              ...cardStyle,
              border: `1px solid ${color}33`,
              boxShadow: `0 12px 24px ${color}1f`,
            }
          : cardStyle
      }
    >
      <div className="small text-secondary mb-1">{label}</div>
      <div className="fw-bold" style={{ color, fontSize: "clamp(1rem, 2.2vw, 1.35rem)" }}>
        {value}
      </div>
      {hint ? (
        <div className="text-secondary" style={{ fontSize: "0.75rem" }}>
          {hint}
        </div>
      ) : null}
    </div>
  </div>
);

// การ์ดที่ย่อ/ขยายได้จากหัวการ์ด (กดที่ชื่อหรือลูกศร) ใช้กับตารางรายละเอียดทุกอันในหน้านี้
const CollapsibleBlock = ({ title, hint, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="p-3 p-md-4 h-100" style={blockStyle}>
      <div className={`d-flex justify-content-between align-items-center gap-2 ${open ? "mb-3" : ""}`}>
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2 text-start"
          style={{ color: "#12314d" }}
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
        >
          <i className={`fa-solid fa-chevron-${open ? "down" : "right"} text-secondary`} style={{ fontSize: "0.7rem", width: 12 }} />
          <h6 className="fw-semibold mb-0">{title}</h6>
        </button>
        {hint ? <span className="small text-secondary text-end">{hint}</span> : null}
      </div>
      {open ? children : null}
    </div>
  );
};

const SummaryRow = ({ label, value, bold, muted }) => (
  <div className="d-flex justify-content-between align-items-center px-3 py-2" style={{ background: muted ? "#f6f8fa" : "transparent" }}>
    <div className={bold ? "fw-semibold" : ""}>{label}</div>
    <div className={bold ? "fw-bold" : ""}>{value}</div>
  </div>
);

// ขั้นตอนที่ 4: ตรวจสอบข้อมูล
// ด้านบนเป็นสรุปแบบเดียวกับหน้า e-Filing ด้านล่างคือรายละเอียดเดิมของระบบ (การ์ดสรุป คำแนะนำ ตารางต่าง ๆ)
export default function StepReview({
  summary,
  taxpayer,
  year,
  projection,
  includeProjection,
  showProjectionDetail,
  setShowProjectionDetail,
  refundRequest,
  setRefundRequest,
}) {
  const incomeRows = summary?.income?.rows || [];
  const deductionRows = summary?.deductions?.rows || [];
  const whtRows = summary?.wht?.rows || [];
  const brackets = summary?.tax_brackets || [];
  const balance = summary?.balance || 0;
  const advice = summary?.advice || [];

  // ประเภทเงินได้ที่กางรายการย่อยอยู่ เริ่มต้นย่อไว้ทั้งหมดให้เห็นภาพรวมก่อน กดที่แถวประเภทเพื่อดูรายการ
  const [expandedCategories, setExpandedCategories] = useState({});
  const toggleCategory = (category) => setExpandedCategories((prev) => ({ ...prev, [category]: !prev[category] }));

  return (
    <>
      <h5 className="fw-semibold mb-3">ตรวจสอบข้อมูล</h5>

      <div className="row g-3 mb-4">
        <div className="col-12 col-xl-6">
          <div className="p-3 p-md-4 h-100" style={formBlockStyle}>
            <div className="fw-semibold mb-3 pb-2 border-bottom" style={{ color: RD_GREEN_DARK }}>
              เงินได้ทั้งหมด
            </div>
            <SummaryRow label={`เงินได้ปีภาษี ${year}`} value={toFixed(summary?.income?.gross_total)} muted />
            <SummaryRow label="หัก ค่าใช้จ่าย" value={toFixed(summary?.income?.expense_total)} />
            <SummaryRow label="หัก ลดหย่อน" value={toFixed(summary?.deductions?.total)} muted />
            <SummaryRow label="รวมเงินได้ที่ถูกนำไปคำนวณภาษี" value={toFixed(summary?.taxable_income)} bold />
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <div className="p-3 p-md-4 h-100" style={formBlockStyle}>
            <div className="fw-semibold mb-3 pb-2 border-bottom" style={{ color: RD_GREEN_DARK }}>
              การคำนวณภาษี
            </div>
            <SummaryRow label="ภาษีที่ต้องชำระ" value={toFixed(summary?.tax_due)} muted />
            <SummaryRow label="หัก ภาษีหัก ณ ที่จ่าย/เครดิตภาษี" value={toFixed(summary?.wht?.total)} />
            <SummaryRow label={balance >= 0 ? "ยอดภาษีสุทธิ ที่ต้องชำระเพิ่ม" : "ยอดภาษีสุทธิ ชำระไว้เกิน"} value={toFixed(Math.abs(balance))} bold />
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <StatCard label="เงินได้รวมทั้งปี" value={toFixed(summary?.income?.gross_total)} color="#0c76e5" hint="เงินได้พึงประเมิน" />
        <StatCard label="หักค่าใช้จ่าย" value={toFixed(summary?.income?.expense_total)} color="#12a5b6" hint="ตามประเภทเงินได้" />
        <StatCard label="หักค่าลดหย่อน" value={toFixed(summary?.deductions?.total)} color="#7a5ad6" hint="ที่ใช้สิทธิ์ได้จริง" />
        <StatCard label="เงินได้สุทธิ" value={toFixed(summary?.taxable_income)} color="#22384a" hint="ฐานคำนวณภาษี" />
        <StatCard
          label="ภาษีทั้งปีที่ต้องเสีย"
          value={toFixed(summary?.tax_due)}
          color="#b97007"
          hint={`อัตราภาษีที่แท้จริง ${toFixed(summary?.effective_rate)}%`}
        />
        <StatCard
          label={balance >= 0 ? "ต้องจ่ายเพิ่ม" : "ขอคืนภาษีได้"}
          value={toFixed(Math.abs(balance))}
          color={balance >= 0 ? "#b63636" : "#188251"}
          hint={`ถูกหัก ณ ที่จ่ายแล้ว ${toFixed(summary?.wht?.total)}`}
          emphasize
        />
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-6">
          <CollapsibleBlock title="รายได้ทั้งปี" hint={'แก้ไขที่ขั้นตอน "กรอกเงินได้"'}>
            <div className="table-responsive">
              {/* ตาราง 2 คอลัมน์: รายการ + รวมเงินได้ ส่วนหักค่าใช้จ่ายและคงเหลือสรุปรวมไว้แถวล่างสุด */}
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="text-secondary fw-medium">รายการ</th>
                    <th className="text-secondary fw-medium text-end text-nowrap">รวมเงินได้</th>
                  </tr>
                </thead>
                <tbody>
                  {incomeRows.length > 0 ? (
                    incomeRows.map((row) => (
                      <React.Fragment key={row.category}>
                        <tr
                          style={{ background: "rgba(12, 118, 229, 0.05)", cursor: "pointer" }}
                          onClick={() => toggleCategory(row.category)}
                          title={expandedCategories[row.category] ? "ย่อรายการ" : "ขยายดูรายการ"}
                        >
                          <td className="fw-semibold" style={{ color: "#0b4f84" }}>
                            <i
                              className={`fa-solid fa-chevron-${expandedCategories[row.category] ? "down" : "right"} text-secondary me-2`}
                              style={{ fontSize: "0.65rem", width: 10 }}
                            />
                            {row.section} {row.label}
                            <span className="text-secondary fw-normal small ms-2">({row.items.length} รายการ)</span>
                          </td>
                          <td className="text-end fw-semibold text-nowrap">{toFixed(row.gross)}</td>
                        </tr>
                        {expandedCategories[row.category] && row.items.map((item, index) => {
                          // แท็กสีที่ติดไว้บนการ์ดในหน้ากรอกเงินได้ ใช้ระบายพื้นหลังแถว (สีขาว/รายการจากสลิป = ไม่ระบาย)
                          const tag =
                            item.source === "manual" && item.color && item.color !== DEFAULT_INCOME_TAG_COLOR
                              ? incomeTagColor(item.color)
                              : null;
                          return (
                          <tr
                            key={item.key || item.uid || `${row.category}-${index}`}
                            style={tag ? { background: tag.soft, boxShadow: `inset 3px 0 0 ${tag.dot}` } : undefined}
                          >
                            <td className="ps-3 small">
                              {/* รายการกรอกเอง: บรรทัด 1 ชื่อรายการ / บรรทัด 2 รายละเอียดที่พิมพ์ไว้ในตัวแก้ไข
                                  รายการจากสลิป/ประมาณการ: บรรทัด 1 บริษัทผู้จ่าย / บรรทัด 2 ชื่องวด */}
                              {/* จุดสีเล็ก ๆ = แท็กสีเดียวกับการ์ดในหน้ากรอกเงินได้ */}
                              {tag ? (
                                <span
                                  className="rounded-circle d-inline-block align-middle me-2"
                                  style={{ width: 9, height: 9, background: tag.dot, border: `1px solid ${tag.border}` }}
                                  title={`แท็กสี: ${tag.label}`}
                                />
                              ) : null}
                              <span className="text-nowrap">{(item.source === "manual" ? item.label : item.payer_name) || item.label}</span>
                              <div className="text-secondary" style={{ fontSize: "0.75rem" }}>
                                {(item.source === "manual" ? item.detail : item.label) || "-"}
                              </div>
                            </td>
                            <td className="text-end small text-nowrap">{toFixed(item.amount)}</td>
                          </tr>
                          );
                        })}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2" className="text-center text-secondary py-4">ยังไม่มีข้อมูลรายได้ในปีภาษีนี้</td>
                    </tr>
                  )}
                </tbody>
                {/* สรุปล่างสุด: รวมเงินได้ทุกประเภท > หักค่าใช้จ่าย > คงเหลือ */}
                <tfoot>
                  <tr style={{ borderTop: "2px solid rgba(18, 125, 255, 0.2)" }}>
                    <td className="fw-bold">รวมเงินได้</td>
                    <td className="text-end fw-bold text-nowrap">{toFixed(summary?.income?.gross_total)}</td>
                  </tr>
                  <tr>
                    <td className="fw-semibold">หักค่าใช้จ่าย</td>
                    <td className="text-end fw-semibold text-nowrap" style={{ color: "#12a5b6" }}>{toFixed(summary?.income?.expense_total)}</td>
                  </tr>
                  <tr style={{ background: "rgba(12, 118, 229, 0.06)" }}>
                    <td className="fw-bold">คงเหลือ</td>
                    <td className="text-end fw-bold text-nowrap" style={{ color: "#0c76e5" }}>{toFixed(summary?.income?.net_total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CollapsibleBlock>
        </div>

        <div className="col-12 col-xl-6">
          <CollapsibleBlock title="ค่าลดหย่อนภาษี" hint={'แก้ไขที่ขั้นตอน "กรอกค่าลดหย่อน"'}>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="text-secondary fw-medium">รายการ</th>
                    <th className="text-secondary fw-medium text-end text-nowrap">ที่กรอก</th>
                    <th className="text-secondary fw-medium text-end text-nowrap">ใช้สิทธิ์ได้</th>
                  </tr>
                </thead>
                <tbody>
                  {deductionRows.map((row) => (
                    <tr key={row.category}>
                      <td>
                        <div className="fw-semibold" style={{ color: "#12314d" }}>
                          {row.label}
                        </div>
                        {row.source !== "manual" ? (
                          <div className="text-secondary" style={{ fontSize: "0.75rem" }}>
                            ระบบคำนวณให้อัตโนมัติ
                          </div>
                        ) : null}
                        {row.capped ? (
                          <div style={{ fontSize: "0.75rem", color: "#b97007" }}>
                            <i className="fa-solid fa-triangle-exclamation me-1" />
                            {row.cap_note}
                          </div>
                        ) : null}
                      </td>
                      <td className="text-end">{toFixed(row.claimed)}</td>
                      <td className="text-end fw-semibold" style={{ color: "#7a5ad6" }}>
                        {toFixed(row.allowed)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: "2px solid rgba(18, 125, 255, 0.2)" }}>
                    <td className="fw-bold">รวมค่าลดหย่อน</td>
                    <td className="text-end fw-bold">{toFixed(summary?.deductions?.claimed_total)}</td>
                    <td className="text-end fw-bold" style={{ color: "#7a5ad6" }}>
                      {toFixed(summary?.deductions?.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {/* กันสับสน: ค่าใช้จ่ายเหมา 50% (ไม่เกิน 100,000) ตามกฎหมายไม่ใช่ค่าลดหย่อน หักไปแล้วตั้งแต่ขั้นเงินได้ */}
            <div className="mt-2 px-1 d-flex gap-2" style={{ fontSize: "0.75rem", color: "#6b8198" }}>
              <i className="fa-solid fa-circle-info mt-1" />
              <span>
                ค่าใช้จ่ายส่วนตัว (หักเหมา 50% ของเงินได้ 40(1)+40(2) รวมกันไม่เกิน 100,000 บาท) ไม่นับเป็นค่าลดหย่อน
                ระบบหักให้แล้วในตาราง "รายได้ทั้งปี" ช่อง <b>หักค่าใช้จ่าย {toFixed(summary?.income?.expense_total)}</b> บาท
              </span>
            </div>
          </CollapsibleBlock>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-6">
          <CollapsibleBlock title="ภาษีที่ถูกหัก ณ ที่จ่ายแล้ว">
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="text-secondary fw-medium">ผู้จ่ายเงินได้</th>
                    <th className="text-secondary fw-medium text-end text-nowrap">เงินได้</th>
                    <th className="text-secondary fw-medium text-end text-nowrap">ถูกหักไว้</th>
                  </tr>
                </thead>
                <tbody>
                  {whtRows.length > 0 ? (
                    whtRows.map((row) => (
                      <tr key={row.payer_name}>
                        <td className="fw-semibold" style={{ color: "#12314d" }}>
                          {row.payer_name}
                        </td>
                        <td className="text-end">{toFixed(row.amount)}</td>
                        <td className="text-end fw-semibold" style={{ color: "#0c76e5" }}>
                          {toFixed(row.wht_amount)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-secondary py-4">
                        ยังไม่มีภาษีถูกหัก ณ ที่จ่าย
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: "2px solid rgba(18, 125, 255, 0.2)" }}>
                    <td colSpan="2" className="fw-bold">
                      รวมเครดิตภาษี
                    </td>
                    <td className="text-end fw-bold" style={{ color: "#0c76e5" }}>
                      {toFixed(summary?.wht?.total)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="small text-secondary mt-3">อย่าลืมเก็บหนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ) ทุกใบไว้ยื่นภาษี</div>
          </CollapsibleBlock>
        </div>

        <div className="col-12 col-xl-6">
          <CollapsibleBlock title="การคำนวณภาษีแบบขั้นบันได" hint={`ฐานคำนวณ ${toFixed(summary?.taxable_income)} บาท`}>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="text-secondary fw-medium">ขั้นเงินได้สุทธิ (บาท)</th>
                    <th className="text-secondary fw-medium text-end">อัตรา</th>
                    <th className="text-secondary fw-medium text-end text-nowrap">เงินได้ในขั้นนี้</th>
                    <th className="text-secondary fw-medium text-end text-nowrap">ภาษีขั้นนี้</th>
                  </tr>
                </thead>
                <tbody>
                  {/* ยอดตั้งต้นที่เอามาไล่ตามขั้น = เงินได้หลังหักค่าใช้จ่ายและค่าลดหย่อน */}
                  <tr style={{ background: "rgba(12, 118, 229, 0.05)" }}>
                    <td colSpan="3" className="fw-semibold" style={{ color: "#0b4f84" }}>
                      รวมเงินได้ที่ถูกนำไปคำนวณภาษี (เงินได้สุทธิ)
                    </td>
                    <td className="text-end fw-bold text-nowrap" style={{ color: "#0b4f84" }}>
                      {toFixed(summary?.taxable_income)}
                    </td>
                  </tr>
                  {brackets.length > 0 ? (
                    brackets.map((row) => (
                      <tr key={`${row.min}-${row.rate}`}>
                        <td className="text-nowrap">
                          {toFixed(row.min, 0)} - {row.max === null ? "ขึ้นไป" : toFixed(row.max, 0)}
                        </td>
                        <td className="text-end">{row.rate}%</td>
                        <td className="text-end">{toFixed(row.amount)}</td>
                        <td className="text-end fw-semibold" style={{ color: "#b97007" }}>
                          {toFixed(row.tax)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-secondary py-4">
                        เงินได้สุทธิยังไม่ถึงเกณฑ์เสียภาษี
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: "2px solid rgba(18, 125, 255, 0.2)" }}>
                    <td colSpan="3" className="fw-bold">
                      ภาษีตามขั้นบันได
                    </td>
                    <td className="text-end fw-bold" style={{ color: "#b97007" }}>
                      {toFixed(summary?.progressive_tax)}
                    </td>
                  </tr>
                  {summary?.minimum_tax?.applied ? (
                    <tr>
                      <td colSpan="3" className="fw-bold" style={{ color: "#b63636" }}>
                        ภาษีขั้นต่ำ 0.5% ของเงินได้ที่ไม่ใช่เงินเดือน ({toFixed(summary.minimum_tax.income_base)})
                      </td>
                      <td className="text-end fw-bold" style={{ color: "#b63636" }}>
                        {toFixed(summary.minimum_tax.amount)}
                      </td>
                    </tr>
                  ) : null}
                  <tr>
                    <td colSpan="3" className="fw-bold">
                      ภาษีที่ต้องเสียทั้งปี
                    </td>
                    <td className="text-end fw-bold" style={{ color: "#b63636" }}>
                      {toFixed(summary?.tax_due)}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="3" className="fw-bold">
                      หัก เครดิตภาษีที่ถูกหักไว้แล้ว
                    </td>
                    <td className="text-end fw-bold" style={{ color: "#188251" }}>
                      -{toFixed(summary?.wht?.total)}
                    </td>
                  </tr>
                  <tr style={{ background: "rgba(12, 118, 229, 0.06)" }}>
                    <td colSpan="3" className="fw-bold">
                      {balance >= 0 ? "ต้องจ่ายภาษีเพิ่ม" : "ขอคืนภาษีได้"}
                    </td>
                    <td className="text-end fw-bold" style={{ color: balance >= 0 ? "#b63636" : "#188251" }}>
                      {toFixed(Math.abs(balance))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CollapsibleBlock>
        </div>
      </div>
    </>
  );
}
